import { useState, useRef } from 'react';
import { extractWatchlistCsvFromZip } from '../lib/zip-extract';

interface ImportResult {
  filmsImported: number;
  filmsProcessed?: number;
  filmsAdded?: number;
  skippedRows: number;
  skippedDuplicates?: number;
  filmsDeleted?: number;
}

interface CsvImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (csvContent: string) => Promise<ImportResult>;
  /** Watchlist mode - determines whether import adds or replaces films */
  mode?: 'manual' | 'sync';
  /** Current number of films in watchlist (for sync mode warning) */
  currentFilmsCount?: number;
}

function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}

function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}

export function CsvImportModal({
  isOpen,
  onClose,
  onImport,
  mode = 'sync',
  currentFilmsCount = 0,
}: CsvImportModalProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [showSyncWarning, setShowSyncWarning] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const isZip = file.name.endsWith('.zip');
    const isCsv = file.name.endsWith('.csv');
    if (!isCsv && !isZip) {
      setError('Please select a CSV or ZIP file');
      return;
    }

    // Validate file size (1MB max)
    if (file.size > 1024 * 1024) {
      setError('File is too large. Maximum size is 1MB.');
      return;
    }

    // In sync mode with existing films, show warning before proceeding
    if (mode === 'sync' && currentFilmsCount > 0) {
      setPendingFile(file);
      setShowSyncWarning(true);
      return;
    }

    // Proceed with import
    await processImport(file);
  };

  const processImport = async (file: File) => {
    setIsUploading(true);
    setError(null);
    setResult(null);
    setShowSyncWarning(false);
    setPendingFile(null);

    try {
      let csvContent: string;
      if (file.name.endsWith('.zip')) {
        const arrayBuffer = await readFileAsArrayBuffer(file);
        csvContent = await extractWatchlistCsvFromZip(arrayBuffer);
      } else {
        csvContent = await readFileAsText(file);
      }
      const importResult = await onImport(csvContent);
      setResult(importResult);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import CSV');
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleConfirmSyncImport = async () => {
    if (pendingFile) {
      await processImport(pendingFile);
    }
  };

  const handleCancelSyncWarning = () => {
    setShowSyncWarning(false);
    setPendingFile(null);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    setError(null);
    setResult(null);
    setShowSyncWarning(false);
    setPendingFile(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg sm:text-xl font-display font-bold text-dark">
            Import from Letterboxd
          </h3>
          <button
            onClick={handleClose}
            className="p-2 text-dark-400 hover:text-dark-600 transition-colors"
            aria-label="Close"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {showSyncWarning && pendingFile ? (
          // Sync mode warning
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-yellow-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h4 className="text-lg font-medium text-dark mb-2">
              Replace Existing Films?
            </h4>
            <p className="text-dark-500 mb-4">
              This will <span className="font-bold text-red-600">replace</span>{' '}
              all <span className="font-bold">{currentFilmsCount}</span> films
              currently in your watchlist with the films from this CSV.
            </p>
            <p className="text-sm text-dark-400 mb-6">
              To add films without replacing, switch to Manual mode first.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={handleCancelSyncWarning}
                className="px-6 py-3 rounded-lg font-medium text-dark-500 hover:bg-dark-50 transition-colors min-h-[44px]"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSyncImport}
                className="bg-red-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-red-700 transition-colors min-h-[44px]"
              >
                Replace All Films
              </button>
            </div>
          </div>
        ) : result ? (
          // Success state
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h4 className="text-lg font-medium text-dark mb-2">
              Import Successful!
            </h4>

            {/* Detailed breakdown */}
            <div className="text-left bg-dark-50 rounded-lg p-4 mb-4 space-y-2">
              <div className="flex justify-between text-dark-600">
                <span>Movies processed:</span>
                <span className="font-medium">
                  {result.filmsProcessed ?? result.filmsImported}
                </span>
              </div>

              {mode === 'manual' ? (
                <>
                  <div className="flex justify-between text-dark-500">
                    <span>Already in your list:</span>
                    <span className="font-medium">
                      {result.skippedDuplicates ?? 0}
                    </span>
                  </div>
                  <div className="flex justify-between text-green-600">
                    <span>Added:</span>
                    <span className="font-bold">{result.filmsAdded ?? 0}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-between text-green-600">
                    <span>Added:</span>
                    <span className="font-bold">{result.filmsAdded ?? 0}</span>
                  </div>
                  {result.filmsDeleted !== undefined &&
                    result.filmsDeleted > 0 && (
                      <div className="flex justify-between text-red-600">
                        <span>Replaced:</span>
                        <span className="font-medium">
                          {result.filmsDeleted}
                        </span>
                      </div>
                    )}
                </>
              )}
            </div>

            {result.skippedRows > 0 && (
              <p className="text-sm text-dark-400 mb-4">
                {result.skippedRows} row(s) were skipped due to invalid data
              </p>
            )}
            <button
              onClick={handleClose}
              className="bg-primary text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors min-h-[44px]"
            >
              Done
            </button>
          </div>
        ) : (
          // Upload state
          <>
            <div className="mb-6">
              <p className="text-dark-500 font-body text-sm sm:text-base mb-4">
                Import your watchlist directly from a Letterboxd export file.
              </p>

              {/* Instructions */}
              <div className="bg-dark-50 rounded-lg p-4 mb-4">
                <h4 className="font-medium text-dark mb-2 text-sm">
                  How to export from Letterboxd:
                </h4>
                <ol className="text-sm text-dark-600 space-y-2 list-decimal list-inside">
                  <li>
                    Go to{' '}
                    <a
                      href="https://letterboxd.com/settings/data/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      Letterboxd Settings → Import & Export
                    </a>
                  </li>
                  <li>Click "Export Your Data"</li>
                  <li>
                    Upload the downloaded ZIP file below (or unzip and select{' '}
                    <code className="bg-dark-100 px-1 rounded">
                      watchlist.csv
                    </code>
                    )
                  </li>
                </ol>
              </div>

              {/* Error display */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              {/* File picker */}
              <div className="relative">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.zip,text/csv,text/plain,application/vnd.ms-excel,application/zip"
                  onChange={handleFileChange}
                  disabled={isUploading}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                  aria-label="Select CSV or ZIP file"
                />
                <div
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    isUploading
                      ? 'border-dark-200 bg-dark-50'
                      : 'border-primary-200 hover:border-primary-400 hover:bg-primary-50'
                  }`}
                >
                  {isUploading ? (
                    <div className="flex items-center justify-center gap-2">
                      <svg
                        className="animate-spin h-5 w-5 text-primary"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      <span className="text-dark-500">Importing...</span>
                    </div>
                  ) : (
                    <>
                      <svg
                        className="w-10 h-10 text-primary-400 mx-auto mb-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                      </svg>
                      <p className="text-dark-600 font-medium">
                        Click to select ZIP or CSV file
                      </p>
                      <p className="text-dark-400 text-sm mt-1">
                        or drag and drop
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end">
              <button
                onClick={handleClose}
                disabled={isUploading}
                className="px-6 py-3 rounded-lg font-medium text-dark-500 hover:bg-dark-50 transition-colors disabled:opacity-50 min-h-[44px]"
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
